/**
 * Her presence on screen.
 *
 * Not a face, by choice. A face sets an expectation the thing behind it cannot
 * meet — it implies someone who understands, at the exact moment the honest
 * message is that this is a tool reading records. It also has to be *someone*,
 * and a product going to an airport operator does not need a stranger's
 * likeness baked into its chrome.
 *
 * So: a mark that behaves. Four states, each distinct at 28px without relying
 * on colour alone, because the dock is read at a glance rather than looked at.
 *
 *   idle       one slow breath — present, not asking for anything
 *   listening  the ring opens and holds — she is taking something in
 *   thinking   the arc travels — work is happening, and it is not instant
 *   speaking   the core pulses on the beat of the words
 *
 * Motion carries meaning here, so it needs a non-motion equivalent: under
 * `prefers-reduced-motion` the animations stop and the states separate by
 * stroke weight and opacity instead. The accessible name says the state in
 * words either way, which is the only version a screen reader ever gets.
 *
 * Three variants and an image, so an operator can make it theirs. `pulse` is
 * the default and the original; the others are the same geometry weighted
 * differently, not three different personalities. An operator's own image is
 * framed in the same circle and dimmed the same way — whatever they choose, the
 * four states still have to be legible, because the states are the useful part.
 */
import { useState } from "react";
import { Avatar } from "./Avatar";
import { DEFAULT_PORTRAIT, Portrait } from "./Portrait";
import s from "./assistant.module.css";
import type { MarkState } from "./types";
import type { MarkVariant } from "./preferences";
import { t } from "../i18n";

const SAYS: Record<MarkState, string> = {
  idle: "Sky, ready",
  listening: "Sky, listening",
  thinking: "Sky, working",
  speaking: "Sky, speaking",
};

export function AssistantMark({
  state,
  size = 28,
  variant = "pulse",
  image,
  className = "",
  onClick,
  title,
}: {
  state: MarkState;
  size?: number;
  variant?: MarkVariant;
  image?: string;
  className?: string;
  onClick?: () => void;
  title?: string;
}) {
  /* `size` means height everywhere else in the dock, so the 3:2 card is asked
     for by width and lands at the same height as the other marks. */
  const [imageFailed, setImageFailed] = useState(false);

  if (variant === "image" && !imageFailed) {
    return (
      <Portrait
        state={state}
        src={image || DEFAULT_PORTRAIT}
        width={Math.round(size * 1.5)}
        className={className}
        onClick={onClick}
        title={title}
        onFailed={() => setImageFailed(true)}
      />
    );
  }
  /* Either she is the drawing by choice, or the picture would not load and the
     drawing is what is left. Both land here. */
  if (variant === "avatar" || variant === "image") {
    return <Avatar state={state} width={Math.round(size * 1.5)} className={className} />;
  }

  return (
    <svg
      className={`${s.mark} ${s[`mark_${state}`]} ${s[`variant_${variant}`]}`}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label={t(SAYS[state])}
    >
      {/* the held ring: the outline of her attention */}
      <circle className={s.markRing} cx="24" cy="24" r="18" fill="none" strokeWidth="1.5" />

      {/* the travelling arc: only visible while she is working */}
      <circle
        className={s.markArc}
        cx="24"
        cy="24"
        r="18"
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray="22 78"
      />

      {/* the core: steady when idle, breathing when she speaks */}
      <circle className={s.markCore} cx="24" cy="24" r={variant === "orbit" ? 4 : 6} />

      {/* three points, the platform's own language of a reading taken */}
      <g className={s.markPoints}>
        <circle cx="24" cy="4.5" r="2" />
        <circle cx="41" cy="33.5" r="2" />
        <circle cx="7" cy="33.5" r="2" />
      </g>
    </svg>
  );
}
